import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IWsResult } from 'src/app/models/ws_result.model';
import { Orders } from 'src/app/models/Orders.model';
import { Products } from 'src/app/models/Product.model';
import { OrdersService } from 'src/app/services/orders.service';
import { ProductsService } from 'src/app/services/products.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';
import {
  SortDirection,
  nextSortState,
  sortItems,
} from 'src/app/utils/table-sort';

/**
 * BorderòComponent — list of generated borderò (delivery receipts
 * signed by the courier).
 *
 * Filters:
 *   • Date range — start / end of "bo_data" (signature date).
 *   • Courier — dropdown populated from the loaded borderò list.
 *   • Search by customer (template still has the hidden form; left
 *     in place for now, will surface in a follow-up).
 *
 * Click on a row opens the BorderòDettagli modal via
 * `openModal.isBordero`.
 *
 * Phase 1.5 / v2.7.0 — TS cleaned up (Chunk C.1):
 *   • dropped ~25 `console.log` / `console.debug`,
 *   • dropped 5 unused @Input copy-paste leftovers from the modal
 *     template (`order_id`, `code`, `isProdOpen`, `product_id` and
 *     `opened` — none of them are read by the new HTML),
 *   • dropped 4 kendo-dialog sizing leftovers (`dialogWidth`,
 *     `dialogHeight`, `maxlength`, `minWidth`) — no kendo-dialog on
 *     this page,
 *   • dropped `LabelSettings` for kendo-progressbar (not used),
 *   • dropped custom `deepCopy` → replaced with native
 *     `structuredClone()` (Node 17+ / ES2022),
 *   • dropped 2 large commented-out blocks (legacy `changeCarier`,
 *     `getOrderDate`) — see git history for the old version,
 *   • dropped unused state: `documents`, `gridView`, `value`,
 *     `indeterminate`, `min`, `max`, `goBack`, `dataSaved`,
 *     `isEditable`, `linkBordero`, `or_url_box_label`,
 *     `bordero` static URL constant, `openOrders`, `formattedDate`,
 *     `pr_code`, `date`, `clickLabel`, `linkAllLabels`,
 *     `clickedProduct`, `canSelectAll`, `hiddenStyle`,
 *     `clickedIdComplete`, `courier_name`, `order_carrier`,
 *     `carrier_id`, `or_sign_date`, `totale`, `ettArray`, `dateBy`,
 *     `range`, `setPercentLabel`,
 *   • dropped unused methods: `goEditFunction`, `productDetails`,
 *     `goToProductDetails`, `getCarrierList` (now inlined as
 *     getter `availableCarriers`), `triggerFunction`, `printBordero`,
 *     `printAllLabels`, `calcolaTotaleImponibile`, `close`,
 *     `openModalDamnIt` (alias of `gotoDetails`).
 *   • switch to `inject()` + `takeUntilDestroyed`.
 *
 * Behaviour preserved 1:1: load borderò list, courier filter, date
 * range filter, search ordersToSign, gotoDetails open-modal flow.
 */
@Component({
  selector: 'app-borderò',
  templateUrl: './borderò.component.html',
  styleUrls: ['./borderò.component.scss'],
  standalone: false,
})


export class BorderòComponent implements OnInit {

  private readonly ordersServ = inject(OrdersService);
  private readonly productServ = inject(ProductsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  public readonly openModal = inject(OpenModalService);

  @Input() opened = false;

  // ── Data ────────────────────────────────────────────────────────
  public orders: Orders.OrderBorderos[] = [];
  public originalOrders: Orders.OrderBorderos[] = [];
  public product: Products.Product[] = [];

  // ── UI state ────────────────────────────────────────────────────
  public loading = false;
  public filterTerm = '';
  public chosenCarrier: string | null = null;
  public selectedBord: string | null = null;
  public clickedOrder = '';
  public readonly showAll = 'Mostra tutti';

  // ── Date range filter (defaults: from 2021 to today) ───────────
  startDate: Date = new Date(2021, 0, 1);
  endDate: Date = new Date();

  filters = {
    dateOrginizedBy: {
      text: 'Data ordine',
      value: 'bo_data',
    },
    startingDate: this.startDate,
    endDate: this.endDate,
    minDate: new Date(),
    maxDate: new Date(),
  };

  public sortColumn: string | null = null;
  public sortDirection: SortDirection = 'asc';

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.refreshFunction();
  }

  refreshFunction(): void {
    this.loading = true;
    this.getBorderos();
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loader.
  // ──────────────────────────────────────────────────────────────────

  private getBorderos(): void {
    this.ordersServ
      .getNewBorderos()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          if (!data) {
            this.loading = false;
            this.cdr.detectChanges();
            return;
          }
          this.originalOrders = data.data ?? [];
          this.orders = data.data ?? [];
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Derived: unique courier list for the filter dropdown.
  //   • starts with "Mostra tutti" sentinel,
  //   • dedupes nulls/empty strings.
  // ──────────────────────────────────────────────────────────────────

  get availableCarriers(): string[] {
    const set = new Set(
      this.originalOrders.map((o) => o.bo_courier_name ?? ''),
    );
    return [this.showAll, ...Array.from(set)];
  }

  // ──────────────────────────────────────────────────────────────────
  // Courier filter — applied to the in-memory list (no backend hop).
  // ──────────────────────────────────────────────────────────────────

  changeCourier(value: string | null): void {
    this.chosenCarrier = value ?? '';

    if (!value || value === this.showAll) {
      this.chosenCarrier = null;
      this.orders = this.originalOrders;
      return;
    }

    const copy = structuredClone(this.originalOrders);
    this.orders = copy
      .filter((item) => item.bo_courier_name === this.chosenCarrier)
      .map(
        (item): Orders.OrderBorderos => ({
          bo_courier_name: item.bo_courier_name,
          bo_data: item.bo_data,
          bo_pdf_link: item.bo_pdf_link,
          bordero_id: item.bordero_id,
          box_number: item.box_number,
          order_number: item.order_number,
        }),
      );
  }

  // ──────────────────────────────────────────────────────────────────
  // Search by customer name. The backend endpoint searches against
  // orders-to-sign; passing an empty term returns the full list.
  // ──────────────────────────────────────────────────────────────────

  searchOrders(): void {
    const term = this.filterTerm?.trim() ?? '';
    this.ordersServ
      .searchOrdersToSign(term || undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: IWsResult) => {
          this.orders = result?.data ?? [];
          this.cdr.detectChanges();
        },
      });
  }

  clearSearch(): void {
    this.filterTerm = '';
    this.searchOrders();
  }

  // ──────────────────────────────────────────────────────────────────
  // Date range filter — applied by the byBordero pipe in the template.
  // ──────────────────────────────────────────────────────────────────

  onStartDateChange(value: Date | string): void {
    const d = value instanceof Date ? value : new Date(value);
    this.filters.startingDate = d;
  }

  onEndDateChange(value: Date | string): void {
    const d = value instanceof Date ? value : new Date(value);
    this.filters.endDate = d;
  }

  // ──────────────────────────────────────────────────────────────────
  // Detail flow — click a row → open BorderòDettagli modal.
  // ──────────────────────────────────────────────────────────────────

  gotoDetails(order_id: string | number): void {
    this.clickedOrder = String(order_id);
    this.openModal.onBordero();
  }

  get filteredBordero(): Orders.OrderBorderos[] {
    const courier = this.selectedBord;
    const start = this.filters.startingDate;
    const end = this.filters.endDate;
    let items = this.orders.filter((o) => {
      if (courier && o.bo_courier_name !== courier) return false;
      if (start && o.bo_data && new Date(o.bo_data) < start) {
        return false;
      }
      if (end && o.bo_data && new Date(o.bo_data) > end) {
        return false;
      }
      return true;
    });
    if (this.sortColumn) {
      items = sortItems(items, this.sortColumn, this.sortDirection);
    }
    return items;
  }

  sortBy(column: string): void {
    const next = nextSortState(this.sortColumn, this.sortDirection, column);
    this.sortColumn = next.column;
    this.sortDirection = next.direction;
    this.cdr.detectChanges();
  }
  
}
